/**
 * t373u: HR documents and right to work (web, desktop and phone). Files open through a 60-second link the API hands out
 * after its access check (each one audited); nothing is cached in the browser beyond the list itself.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HR_REVEAL_SECONDS, hrDocProblem, type HrDocument, type HrDocumentInput, type HrRtwInput } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useWorkspaceId } from '@/lib/hooks';
import { uploadFile } from '@/lib/upload';
import { useUi } from '@/lib/store';
import { hrKey, refreshHr } from './hooks';

/** `who` is 'me' or a user id. */
export function useHrDocs(who: string, enabled = true) {
  const ws = useWorkspaceId();
  const { userId: me } = useAuth();
  const id = who === 'me' ? me : who;
  const q = useQuery({ queryKey: hrKey(ws, `docs:${id}`), queryFn: () => api.hr.documents(ws!, id!), enabled: Boolean(ws && id) && enabled, retry: false, staleTime: 0 });
  return { ...q, userId: id };
}
export const useHrExpiring = (enabled = true) => {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: hrKey(ws, 'expiring'), queryFn: () => api.hr.expiring(ws!), enabled: Boolean(ws) && enabled, retry: false });
};

export function useHrDocActions(userId: string | null | undefined) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [busy, setBusy] = useState(false);
  const run = async <T,>(f: () => Promise<T>, ok?: string): Promise<T | null> => {
    setBusy(true);
    try { const r = await f(); if (ok) toast(ok, 'success'); void refreshHr(ws); return r; } catch (e) { toast(errorMessage(e, 'Couldn’t do that'), 'error'); return null; } finally { setBusy(false); }
  };
  return {
    busy,
    /** Checks the type and size before sending any bytes. */
    upload: (file: File, meta: HrDocumentInput) => {
      const problem = hrDocProblem(file);
      if (problem) { toast(problem, 'error'); return Promise.resolve(null); }
      return run(async () => {
        const a = await uploadFile(file, { workspace_id: ws!, purpose: 'hr_document' });
        return api.hr.addDocument(ws!, userId!, { ...meta, attachment_id: a.id });
      }, 'Document saved');
    },
    update: (d: HrDocument, body: HrDocumentInput, ok = 'Saved') => run(() => api.hr.updateDocument(ws!, userId!, d.id, body), ok),
    remove: (d: HrDocument, note?: string | null) => run(() => api.hr.deleteDocument(ws!, userId!, d.id, note), 'Document deleted'),
    open: (d: HrDocument, download = false) => run(async () => {
      const r = await api.hr.docUrl(ws!, userId!, d.id, download);
      window.open(r.url, '_blank', 'noopener');
      return r;
    }),
    addCheck: (body: HrRtwInput) => run(() => api.hr.addCheck(ws!, userId!, body), 'Right-to-work check saved'),
    deleteCheck: (id: string, note: string) => run(() => api.hr.deleteCheck(ws!, userId!, id, note), 'Check deleted'),
  };
}

/** Show a share code for 30 seconds (audited), then mask it again; leaving the tab hides it at once. */
export function useShareCodeReveal(userId: string | null | undefined) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [shown, setShown] = useState<{ id: string; value: string; until: number } | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!shown) return;
    const t = setInterval(() => { const n = Date.now(); setNow(n); if (n >= shown.until) setShown(null); }, 1000);
    const hide = () => { if (document.visibilityState === 'hidden') setShown(null); };
    document.addEventListener('visibilitychange', hide);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', hide); };
  }, [shown]);
  return {
    value: (id: string) => (shown?.id === id ? shown.value : null),
    left: shown ? Math.max(0, Math.ceil((shown.until - now) / 1000)) : 0,
    reveal: async (id: string) => {
      try {
        const r = await api.hr.revealShareCode(ws!, userId!, id);
        setShown({ id, value: r.value, until: Date.now() + (r.hide_after_seconds || HR_REVEAL_SECONDS) * 1000 });
      } catch (e) { toast(errorMessage(e, 'Couldn’t show this'), 'error'); }
    },
    hide: () => setShown(null),
  };
}
